import { useState } from "react";
import api from "../api";
import { useConfirm } from "./ConfirmDialog";

function issueAccent(s) {
  return s === "solved" ? "a-green" : s === "blocked" ? "a-red" : "a-amber";
}

export default function QuestionsPanel({ state, setState, isAdmin }) {
  const confirm = useConfirm();
  const [inp, setInp] = useState("");
  const [filter, setFilter] = useState({ pending: true, blocked: true, solved: false });
  const [sort, setSort] = useState("dateDesc"); // dateDesc | dateAsc
  const [editing, setEditing] = useState(null);
  const [edit, setEdit] = useState({});

  const replace = (questions) => setState({ ...state, questions });

  const add = async () => {
    if (!inp.trim()) return;
    const body = { title: inp.trim(), text: "", status: "pending" };
    const { data } = await api.post("/questions", body);
    replace([...state.questions, data]);
    setInp("");
  };

  const del = async (i) => {
    const q = state.questions[i];
    const ok = await confirm({
      title: "Delete this issue?",
      message: `"${q?.title || ""}"\n\nThis cannot be undone.`,
      confirmLabel: "Delete",
    });
    if (!ok) return;
    await api.delete(`/questions/${i}`);
    replace(state.questions.filter((_, j) => j !== i));
  };

  const startEdit = (i) => {
    setEditing(i);
    setEdit({ ...state.questions[i] });
  };

  const setStatus = async (i, st) => {
    const { data } = await api.patch(`/questions/${i}`, { status: st });
    replace(state.questions.map((x, j) => (j === i ? { ...x, ...data } : x)));
  };

  const save = async () => {
    const i = editing;
    const { data } = await api.patch(`/questions/${i}`, { title: edit.title, text: edit.text });
    replace(state.questions.map((x, j) => (j === i ? { ...x, ...data } : x)));
    setEditing(null);
  };

  const rows = state.questions
    .map((q, i) => ({ q, i }))
    .filter((r) => filter[r.q.status])
    .sort((a, b) => {
      const da = a.q.createdAt || "0000-00-00";
      const dbb = b.q.createdAt || "0000-00-00";
      // Stable secondary sort by index to keep equal dates in insertion order
      if (da === dbb) return sort === "dateAsc" ? a.i - b.i : b.i - a.i;
      return sort === "dateAsc" ? da.localeCompare(dbb) : dbb.localeCompare(da);
    });

  return (
    <div data-testid="panel-questions">
      <div className="sec-head">Issues List</div>
      <div className="sec-desc">
        Open decisions, blockers, and unresolved questions. Solved issues are hidden by default.
      </div>
      {isAdmin && (
        <div className="add-row">
          <input
            className="grow"
            placeholder="New issue..."
            value={inp}
            onChange={(e) => setInp(e.target.value)}
            data-testid="question-input"
          />
          <button className="btn" onClick={add} data-testid="question-add">
            Add
          </button>
        </div>
      )}
      <div className="filter-row">
        <span className="flabel">Show</span>
        {["pending", "blocked", "solved"].map((s) => (
          <button
            key={s}
            className={`chip-toggle fc-${s} ${filter[s] ? "on" : ""}`}
            onClick={() => setFilter({ ...filter, [s]: !filter[s] })}
            data-testid={`filter-${s}`}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
        <span className="flabel" style={{ marginLeft: 8 }}>Sort</span>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          data-testid="question-sort"
        >
          <option value="dateDesc">Newest first</option>
          <option value="dateAsc">Oldest first</option>
        </select>
      </div>
      {rows.length === 0 ? (
        <div className="empty-note">No issues match the selected filters.</div>
      ) : (
        rows.map(({ q, i }) => (
          <div className="item" key={q.id || i} data-testid={`question-${i}`}>
            <div className={`item-accent ${issueAccent(q.status)}`} />
            <div className="item-main">
              {editing === i ? (
                <>
                  <input
                    className="inline-edit"
                    value={edit.title}
                    onChange={(e) => setEdit({ ...edit, title: e.target.value })}
                  />
                  <textarea
                    className="inline-edit"
                    value={edit.text}
                    onChange={(e) => setEdit({ ...edit, text: e.target.value })}
                  />
                  <div className="status-row">
                    {["pending", "blocked", "solved"].map((s) => (
                      <button
                        key={s}
                        className={q.status === s ? `sel-${s}` : ""}
                        onClick={() => setStatus(i, s)}
                      >
                        {s.charAt(0).toUpperCase() + s.slice(1)}
                      </button>
                    ))}
                  </div>
                  <button
                    className="btn btn-sm"
                    style={{ marginTop: 8 }}
                    onClick={save}
                  >
                    Save text
                  </button>
                </>
              ) : (
                <>
                  <div className="item-title">
                    {q.title}{" "}
                    <span className={`pill ${q.status}`}>
                      {q.status.charAt(0).toUpperCase() + q.status.slice(1)}
                    </span>
                  </div>
                  <div className="item-text">{q.text}</div>
                  {q.createdAt && (
                    <span className="item-tag" data-testid={`question-${i}-date`}>
                      Added {q.createdAt}
                    </span>
                  )}
                </>
              )}
            </div>
            {isAdmin && (
              <>
                <button
                  className="edit-pencil"
                  onClick={() => startEdit(i)}
                  data-testid={`question-${i}-edit`}
                >
                  ✎
                </button>
                <button
                  className="del-x"
                  onClick={() => del(i)}
                  data-testid={`question-${i}-delete`}
                >
                  ×
                </button>
              </>
            )}
          </div>
        ))
      )}
    </div>
  );
}
